// loader.ts - runs on customer page

(function () {
    type OrgData = { org_id: string; widget_token: string };
    type BootMessage = {
        type: 'z360:boot';
        organization: OrgData;
        parentOrigin: string;
        baseUrl: string;
    };
    const MOBILE_BREAKPOINT_PX = 640;
    const CLOSED_WIDGET_SIZE_PX = 64;

    try {
        const currentScript = getCurrentScript();
        const organization = parseOrganization(currentScript);

        // Determine base HREF for the iframe
        const baseDir = resolveBaseHref(currentScript);

        // Capture the origin of the loader script once
        const scriptOrigin = new URL(currentScript.src).origin;

        const iframe = injectIframe(baseDir);
        initIframe(iframe, organization, scriptOrigin);
        attachIframeListeners(iframe);
    } catch (e) {
        console.error('[Z360] loader:', e);
    }

    // Helper functions (placed below boot sequence)
    function getCurrentScript(): HTMLScriptElement {
        const cs = document.currentScript as HTMLScriptElement | null;
        if (cs) return cs;
        const scripts = document.getElementsByTagName('script');
        const el = scripts[scripts.length - 1] as HTMLScriptElement | undefined;
        if (!el) throw new Error('Unable to locate current script element');
        return el;
    }

    function parseOrganization(el: HTMLScriptElement): OrgData {
        const rawOrg = el.getAttribute('data-organization');
        if (!rawOrg) throw new Error('Missing data-organization attribute');
        let parsed: unknown;
        try {
            parsed = JSON.parse(rawOrg);
        } catch {
            throw new Error('Invalid JSON in data-organization');
        }
        if (!parsed || typeof parsed !== 'object' || !('org_id' in parsed)) throw new Error('Missing required org_id in organization');
        if (!('widget_token' in parsed)) throw new Error('Missing required widget_token in organization');
        return parsed as OrgData;
    }

    // Storage and ID management moves to iframe. Loader only proxies localStorage.

    function resolveBaseHref(el: HTMLScriptElement): string {
        // Optional opt-in dev server override for HMR
        const devServer = (el.getAttribute('data-dev-server') || '').trim();
        if (devServer) {
            try {
                const url = new URL(devServer, window.location.href);
                // Ensure trailing slash
                let href = url.href;
                if (!href.endsWith('/')) href += '/';
                return href;
            } catch {
                console.warn('[Z360] Invalid data-dev-server value; falling back to built assets');
            }
        }
        // Default: use the same directory as the loader script (built assets in /public/widget)
        const scriptUrl = new URL(el.src, window.location.href);
        return scriptUrl.href.substring(0, scriptUrl.href.lastIndexOf('/') + 1);
    }

    function injectIframe(baseHref: string): HTMLIFrameElement {
        const iframe = document.createElement('iframe');
        iframe.title = 'Z360 Widget';
        iframe.sandbox = 'allow-scripts allow-same-origin allow-forms allow-popups';
        iframe.style.position = 'fixed';
        positionIframe(iframe);
        iframe.style.border = '0';
        iframe.style.zIndex = '2147483647';
        iframe.style.width = `${CLOSED_WIDGET_SIZE_PX}px`;
        iframe.style.height = `${CLOSED_WIDGET_SIZE_PX}px`;
        iframe.style.background = 'transparent';
        iframe.style.transition = '.01s height linear, .01s width linear';
        iframe.src = baseHref + 'index.html';
        if (!document.body) throw new Error('Document body not ready');
        document.body.appendChild(iframe);
        return iframe;
    }

    function initIframe(iframe: HTMLIFrameElement, organization: OrgData, scriptOrigin: string) {
        iframe.addEventListener('load', () => {
            const targetOrigin = new URL(iframe.src).origin;
            const message: BootMessage = {
                type: 'z360:boot',
                organization,
                parentOrigin: window.location.origin,
                baseUrl: scriptOrigin,
            };
            iframe.contentWindow?.postMessage(message, targetOrigin);
        });
    }

    function positionIframe(iframe: HTMLIFrameElement) {
        const viewport = window.visualViewport;
        const width = viewport?.width ?? window.innerWidth;
        const isMobile = width < MOBILE_BREAKPOINT_PX;
        const isOpen = Math.max(Number.parseFloat(iframe.style.width), Number.parseFloat(iframe.style.height)) > CLOSED_WIDGET_SIZE_PX;
        if (isMobile && isOpen) {
            iframe.style.top = `${viewport?.offsetTop ?? 0}px`;
            iframe.style.left = `${viewport?.offsetLeft ?? 0}px`;
            iframe.style.right = '0px';
            iframe.style.bottom = '0px';
            iframe.style.width = `${width}px`;
            iframe.style.height = `${viewport?.height ?? window.innerHeight}px`;
            return;
        }
        iframe.style.top = '';
        iframe.style.left = '';
        // The widget includes 8px of shadow padding inside the iframe.
        const offset = (isMobile ? 16 : 24) - 8;
        const right = document.documentElement.clientWidth - (width + (viewport?.offsetLeft ?? 0));
        const bottom = window.innerHeight - ((viewport?.height ?? window.innerHeight) + (viewport?.offsetTop ?? 0));
        iframe.style.right = `${Math.max(0, right) + offset}px`;
        iframe.style.bottom = `${Math.max(0, bottom) + offset}px`;
    }

    function sendViewportDimensions(iframe: HTMLIFrameElement) {
        const targetOrigin = new URL(iframe.src).origin;
        iframe.contentWindow?.postMessage(
            {
                type: 'z360:viewport',
                width: window.visualViewport?.width ?? window.innerWidth,
                height: window.visualViewport?.height ?? window.innerHeight,
            },
            targetOrigin,
        );
    }

    function attachIframeListeners(iframe: HTMLIFrameElement) {
        let activated = false;
        let removeActivationListeners: (() => void) | null = null;
        let iframeReady = false;

        const getIframeOrigin = (): string | null => {
            try {
                return new URL(iframe.src, window.location.href).origin;
            } catch {
                return null;
            }
        };

        const post = (origin: string, message: unknown) => {
            iframe.contentWindow?.postMessage(message, origin);
        };

        const triggerActivation = (origin: string) => {
            // Cleanup only once, but always notify (handles late subscribe / iframe reload).
            if (!activated) {
                activated = true;
                removeActivationListeners?.();
                removeActivationListeners = null;
            }
            post(origin, { type: 'z360:activation:triggered' });
        };

        const hasBeenActive = () => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const ua = (navigator as any)?.userActivation;
            return Boolean(ua?.hasBeenActive);
        };

        const ensureActivationListeners = (origin: string) => {
            if (activated || hasBeenActive()) {
                triggerActivation(origin);
                return;
            }
            if (removeActivationListeners) return;

            const optsPassive: AddEventListenerOptions = { capture: true, once: true, passive: true };
            const opts: AddEventListenerOptions = { capture: true, once: true };

            const onFirstInteraction = () => triggerActivation(origin);

            window.addEventListener('pointerdown', onFirstInteraction, optsPassive);
            window.addEventListener('touchstart', onFirstInteraction, optsPassive);
            window.addEventListener('keydown', onFirstInteraction, opts);

            removeActivationListeners = () => {
                window.removeEventListener('pointerdown', onFirstInteraction, optsPassive);
                window.removeEventListener('touchstart', onFirstInteraction, optsPassive);
                window.removeEventListener('keydown', onFirstInteraction, opts);
            };
        };

        const stopActivationListeners = () => {
            removeActivationListeners?.();
            removeActivationListeners = null;
        };

        const sendViewportIfReady = () => {
            positionIframe(iframe);
            if (!iframeReady) return;
            sendViewportDimensions(iframe);
        };

        let resizeTimeout: number | undefined;
        window.addEventListener('resize', () => {
            if (!iframeReady) return;
            clearTimeout(resizeTimeout);
            resizeTimeout = window.setTimeout(() => sendViewportIfReady(), 150);
        });

        window.visualViewport?.addEventListener('resize', sendViewportIfReady);
        window.visualViewport?.addEventListener('scroll', sendViewportIfReady);

        const onMessage = (event: MessageEvent) => {
            if (event.source !== iframe.contentWindow) return;

            const origin = getIframeOrigin();
            if (!origin || event.origin !== origin) return;

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const data = event.data as any;
            if (!data || typeof data !== 'object') return;

            switch (data.type) {
                case 'z360:resize': {
                    const hasWidth = data.width != null;
                    const hasHeight = data.height != null;
                    const w = Math.max(0, Math.ceil(Number(data.width ?? 0)));
                    const h = Math.max(0, Math.ceil(Number(data.height ?? 0)));

                    iframe.style.width = `${hasWidth ? w : CLOSED_WIDGET_SIZE_PX}px`;
                    iframe.style.height = `${hasHeight ? h : CLOSED_WIDGET_SIZE_PX}px`;
                    positionIframe(iframe);
                    break;
                }
                case 'z360:ready': {
                    iframeReady = true;
                    sendViewportIfReady();
                    break;
                }

                case 'z360:launcher:ready': {
                    // Public hook for host pages with a placeholder launcher: swap it out on this event
                    // (or when the attribute appears), not when the iframe element shows up.
                    iframe.setAttribute('data-z360-ready', 'true');
                    window.dispatchEvent(new CustomEvent('z360:widget:ready'));
                    break;
                }

                case 'z360:storage': {
                    const action = data.action as 'get' | 'set' | 'remove';
                    const key = typeof data.key === 'string' ? data.key : '';
                    const value = typeof data.value === 'string' ? data.value : '';
                    const reqId = data.reqId;

                    try {
                        // Session-scoped keys (open/teaser state) reset per browser session; everything
                        // else (visitor id, appearance cache) persists.
                        const store = data.storage === 'session' ? sessionStorage : localStorage;
                        let result: string | null = null;

                        if (action === 'get') result = store.getItem(key);
                        else if (action === 'set') store.setItem(key, value);
                        else if (action === 'remove') store.removeItem(key);
                        else return;

                        post(origin, { type: 'z360:storage:ok', reqId, value: result });
                    } catch (err: unknown) {
                        post(origin, {
                            type: 'z360:storage:error',
                            reqId,
                            message: err instanceof Error ? err.message : String(err),
                        });
                    }
                    break;
                }

                case 'z360:activation:subscribe':
                    ensureActivationListeners(origin);
                    break;

                case 'z360:activation:unsubscribe':
                    stopActivationListeners();
                    break;
            }
        };

        window.addEventListener('message', onMessage);
    }
})();
